import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import {useSelector} from 'react-redux';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ComplexPolymerContentsQuery,
  ComplexPolymerContentsQueryVariables,
} from 'modbio/complex-polymers/__generated/ComplexPolymerContentsQuery';
import {
  ComplexPolymerType,
  COMPLEX_POLYMER_DISPLAY_NAMES,
  COMPLEX_POLYMER_CONTENTS_QUERY,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  getResolvedAttachmentsForOligoDuplex,
  isOligoConjugate,
  isOligoConjugateVersion,
  isOligoDuplex,
  isOligoDuplexVersion,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {capitalize} from 'util/NameUtil';

import ComplexPolymerDragAndDropCanvas from '../drag-and-drop/ComplexPolymerDragAndDropCanvas';

export default function ComplexPolymerMap(props: {
  complexPolymerId: string;
  isEditable: boolean;
}): JSX.Element | null {
  const openComplexPolymerVersionId = useSelector(
    (state: BenchlingState) =>
      state.ui.modbio.complexPolymerVersionUIs[props.complexPolymerId].complexPolymerVersionId
  );

  return (
    <div className={classNames('react-tab-container', css(styles.dragAndDropCanvas))}>
      <BenchlingQuery<ComplexPolymerContentsQuery, ComplexPolymerContentsQueryVariables>
        query={COMPLEX_POLYMER_CONTENTS_QUERY}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
        variables={{complexPolymerId: props.complexPolymerId, versionId: openComplexPolymerVersionId}}
      >
        {(data) => {
          if (isInaccessible(data.complexPolymer)) {
            return (
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text="You do not have permission to view this complex polymer"
              />
            );
          }

          if (isOligoConjugate(data.complexPolymer)) {
            assert(
              data.complexPolymer.version != null &&
                data.complexPolymer.currentVersion != null &&
                isOligoConjugateVersion(data.complexPolymer.version),
              'Expected complex polymer version to exist'
            );

            if (data.complexPolymer.version.oligo == null) {
              return (
                <InlineBanner
                  bannerType={InlineBannerType.WARNING}
                  text={`${capitalize(
                    COMPLEX_POLYMER_DISPLAY_NAMES[ComplexPolymerType.OLIGO_CONJUGATE]
                  )} structure is empty`}
                />
              );
            }

            const isLatest = openComplexPolymerVersionId === data.complexPolymer.currentVersion.id;

            return (
              <ComplexPolymerDragAndDropCanvas
                key={openComplexPolymerVersionId}
                isEditable={isLatest && props.isEditable}
                complexPolymerType={ComplexPolymerType.OLIGO_CONJUGATE}
                complexPolymerId={props.complexPolymerId}
                versionId={openComplexPolymerVersionId}
                simplePolymers={data.complexPolymer.version.structure!.polymers}
                connections={data.complexPolymer.version.structure!.connections}
                conjugates={data.complexPolymer.version.conjugates}
                resolvedAttachments={data.complexPolymer.version.resolvedAttachments}
                forwardOffset={null}
                structureGraph={data.complexPolymer.version.structureGraph}
                outOfSync={data.complexPolymer.version.outOfSync}
                outOfSyncReason={data.complexPolymer.version.outOfSyncReason}
              />
            );
          }

          if (isOligoDuplex(data.complexPolymer)) {
            assert(
              data.complexPolymer.version != null &&
                data.complexPolymer.currentVersion != null &&
                isOligoDuplexVersion(data.complexPolymer.version),
              'Expected complex polymer version to exist'
            );

            if (
              data.complexPolymer.version.forwardStrand == null ||
              data.complexPolymer.version.reverseStrand == null ||
              data.complexPolymer.version.offset == null
            ) {
              return (
                <InlineBanner
                  bannerType={InlineBannerType.WARNING}
                  text={`${capitalize(
                    COMPLEX_POLYMER_DISPLAY_NAMES[ComplexPolymerType.OLIGO_DUPLEX]
                  )} structure cannot be visualized`}
                />
              );
            }

            const isLatest = openComplexPolymerVersionId === data.complexPolymer.currentVersion.id;

            return (
              <ComplexPolymerDragAndDropCanvas
                key={openComplexPolymerVersionId}
                isEditable={props.isEditable && isLatest}
                complexPolymerType={ComplexPolymerType.OLIGO_DUPLEX}
                complexPolymerId={props.complexPolymerId}
                versionId={openComplexPolymerVersionId}
                simplePolymers={data.complexPolymer.version.structure!.polymers}
                connections={data.complexPolymer.version.structure!.connections}
                conjugates={[
                  ...data.complexPolymer.version.forwardStrand.conjugates,
                  ...data.complexPolymer.version.reverseStrand.conjugates,
                ]}
                resolvedAttachments={getResolvedAttachmentsForOligoDuplex(
                  data.complexPolymer.version.forwardStrand,
                  data.complexPolymer.version.reverseStrand
                )}
                forwardOffset={data.complexPolymer.version.offset}
                structureGraph={data.complexPolymer.version.structureGraph}
                outOfSync={data.complexPolymer.version.outOfSync}
                outOfSyncReason={data.complexPolymer.version.outOfSyncReason}
              />
            );
          }

          assertAndContinue(false, 'Expected complex polymer entity');
          return <InlineBanner bannerType={InlineBannerType.ERROR} text="Entity is not a complex polymer" />;
        }}
      </BenchlingQuery>
    </div>
  );
}

const styles = StyleSheet.create({
  tabPadding: {
    paddingBottom: 0,
  },
  dragAndDropCanvas: {
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
